import React, { useState, useCallback, useEffect, useRef } from "react";
import config from "./config";
import { useTheme } from "./ThemeContext"; // Import useTheme

/**
 * AudioPlayerNextChapter - A standalone component for chapter transitions
 * Handles both generating new chapters and navigating to existing ones,
 * plus collecting user feedback.
 *
 * @param {Object} props
 * @param {Object} props.currentBook - The current book object
 * @param {number} props.nextChapter - The number of the next chapter to consider
 * @param {Function} props.onGenerated - Callback function to call after successful generation
 * @param {Function} props.onPlayNext - Callback function to play the next chapter
 * @param {boolean} props.visible - Whether the component should be visible
 */
const AudioPlayerNextChapter = ({
   currentBook,
   nextChapter,
   onGenerated,
   onPlayNext,
   visible = false,
}) => {
   // Get current theme from context
   const { theme } = useTheme();
   const isLightTheme = theme === "light";

   // Local state to track generation status
   const [isGenerating, setIsGenerating] = useState(false);
   const [error, setError] = useState(null);
   const [isAllowedToGenerate, setIsAllowedToGenerate] = useState(false);
   const [showPermissionMessage, setShowPermissionMessage] = useState(false);
   const [nextChapterExists, setNextChapterExists] = useState(false);

   // State for feedback questions
   const [feedbackQuestion, setFeedbackQuestion] = useState("");
   const [showFeedback, setShowFeedback] = useState(false);
   const [feedbackRating, setFeedbackRating] = useState(0);
   const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
   const [alreadyAnswered, setAlreadyAnswered] = useState(false);
   const [feedbackCompleted, setFeedbackCompleted] = useState(false);

   // Generation WebSocket tracking state
   const [generationTaskUuid, setGenerationTaskUuid] = useState(null);
   const [isReadyForWs, setIsReadyForWs] = useState(false);
   const [generationUpdates, setGenerationUpdates] = useState([]);
   const [finalStatus, setFinalStatus] = useState(null);
   const [generationExpanded, setGenerationExpanded] = useState(false);

   // Refs for WebSocket management
   const wsRef = useRef(null);
   const isClosedRef = useRef(false);
   const intentionalCloseRef = useRef(false);
   const isConnectedRef = useRef(false);
   const mountedRef = useRef(true);

   // Debug logging
   useEffect(() => {
      console.log("AudioPlayerNextChapter props:", {
         nextChapter,
         currentBook: currentBook?.title,
         visible,
         nextChapterExists,
      });
   }, [nextChapter, currentBook, visible, nextChapterExists]);

   useEffect(() => {
      if (visible) {
         console.log(`🔎 AudioPlayerNextChapter became VISIBLE`);
         console.log(
            `Book has ${
               currentBook?.num_generated_chapters || 0
            } generated chapters`
         );
         console.log(`Next chapter to consider: ${nextChapter}`);
         console.log(`nextChapterExists: ${nextChapterExists}`);
      } else {
         console.log(`🔎 AudioPlayerNextChapter became HIDDEN`);
      }
   }, [
      visible,
      currentBook?.num_generated_chapters,
      nextChapter,
      nextChapterExists,
   ]);

   // Check if the user is allowed to generate the next chapter
   // and if the next chapter already exists
   const checkPermissionAndStatus = useCallback(async () => {
      if (!currentBook || !visible || !nextChapter) return;

      // FIRST check if we've exceeded the total number of chapters
      const totalChapters = currentBook.num_total_chapters || 1;
      const nextChapterNumber = nextChapter;

      console.log(
         `Checking next chapter status: Chapter ${nextChapterNumber}, Total: ${totalChapters}`
      );

      // If we've exceeded the total number of chapters, don't show any UI
      if (nextChapterNumber > totalChapters) {
         console.log(
            `❌ Chapter ${nextChapterNumber} exceeds total chapters (${totalChapters})`
         );
         setNextChapterExists(false);
         setShowPermissionMessage(false);
         setIsAllowedToGenerate(false);
         return;
      }

      // Now, check if the next chapter already exists
      const numGeneratedChapters = currentBook.num_generated_chapters || 0;

      console.log(
         `Next chapter: ${nextChapterNumber}, Generated: ${numGeneratedChapters}`
      );

      // If the next chapter number is less than or equal to the number of generated chapters,
      // then it already exists and we don't need to generate it
      if (nextChapterNumber <= numGeneratedChapters) {
         console.log(
            `✅ Chapter ${nextChapterNumber} already exists - showing play button`
         );
         setNextChapterExists(true);
         setShowPermissionMessage(false);
         setIsAllowedToGenerate(false); // Not relevant when chapter exists
         return;
      } else {
         console.log(`❌ Chapter ${nextChapterNumber} does not exist yet`);
         setNextChapterExists(false);
      }

      try {
         setError(null);
         const token = localStorage.getItem("token");
         if (!token) {
            console.log("No token found for permission check");
            setIsAllowedToGenerate(false);
            return;
         }

         console.log(`Checking permission for book: ${currentBook.uuid}`);

         const bookUuid = currentBook.uuid;
         const apiPath = `/api/user/allow_new_chapter/${bookUuid}`;

         console.log(`Checking permission at: ${apiPath}`);

         const response = await fetch(apiPath, {
            method: "GET",
            headers: {
               Authorization: `Bearer ${token}`,
               "Content-Type": "application/json",
               "Cache-Control": "no-cache",
            },
         });

         if (!response.ok) {
            console.error(`Failed to check permission: ${response.status}`);
            setIsAllowedToGenerate(false);

            try {
               const errorText = await response.text();
               console.error(`Error details: ${errorText}`);
            } catch (textError) {
               console.error("Could not read error response");
            }
            return;
         }

         const allowed = await response.json();
         setIsAllowedToGenerate(allowed);
         setShowPermissionMessage(!allowed);
         console.log(
            `User is ${allowed ? "" : "not "}allowed to generate next chapter`
         );
      } catch (err) {
         console.error("Error checking permission:", err);
         setIsAllowedToGenerate(false);
      }
   }, [currentBook, visible, nextChapter]);

   // Fetch feedback question for the current chapter
   const fetchFeedbackQuestion = useCallback(async () => {
      if (!currentBook || !visible || feedbackCompleted) return;

      // Get the current chapter (not the next one)
      const currentChapterNumber = nextChapter - 1;
      if (currentChapterNumber < 1) return;

      try {
         const token = localStorage.getItem("token");
         if (!token) {
            console.log("No token found for feedback question");
            return;
         }

         console.log(
            `Fetching feedback question for book: ${currentBook.uuid}, chapter: ${currentChapterNumber}`
         );

         const apiPath = `/api/user/get_chapter_improvement_question/${currentBook.uuid}/${currentChapterNumber}`;
         console.log(`Fetching feedback question from: ${apiPath}`);

         const response = await fetch(apiPath, {
            method: "GET",
            headers: {
               Authorization: `Bearer ${token}`,
               "Content-Type": "application/json",
               "Cache-Control": "no-cache",
            },
         });

         if (!response.ok) {
            console.error(
               `Failed to fetch feedback question: ${response.status}`
            );
            setShowFeedback(false);
            return;
         }

         const data = await response.json();
         console.log("Feedback question response:", data);

         if (data.already_answered) {
            setAlreadyAnswered(true);
            setShowFeedback(false);
            setFeedbackCompleted(true);
         } else if (data.question) {
            setFeedbackQuestion(data.question);
            setShowFeedback(true);
            setAlreadyAnswered(false);
         } else {
            setShowFeedback(false);
            setFeedbackCompleted(true);
         }
      } catch (err) {
         console.error("Error fetching feedback question:", err);
         setShowFeedback(false);
      }
   }, [currentBook, visible, nextChapter, feedbackCompleted]);

   // Submit feedback
   const submitFeedback = useCallback(async () => {
      if (!currentBook || !feedbackQuestion || feedbackRating === 0) return;

      // Get the current chapter (not the next one)
      const currentChapterNumber = nextChapter - 1;
      if (currentChapterNumber < 1) return;

      try {
         setIsSubmittingFeedback(true);

         const token = localStorage.getItem("token");
         if (!token) {
            console.log("No token found for feedback submission");
            setIsSubmittingFeedback(false);
            return;
         }

         console.log(
            `Submitting feedback for book: ${currentBook.uuid}, chapter: ${currentChapterNumber}`
         );

         const apiPath = `/api/user/submit_improvement_rating/${currentBook.uuid}/${currentChapterNumber}`;
         console.log(`Submitting feedback to: ${apiPath}`);

         const response = await fetch(apiPath, {
            method: "POST",
            headers: {
               Authorization: `Bearer ${token}`,
               "Content-Type": "application/json",
            },
            body: JSON.stringify({
               rating: feedbackRating,
               question: feedbackQuestion,
            }),
         });

         if (!response.ok) {
            console.error(`Failed to submit feedback: ${response.status}`);
            setIsSubmittingFeedback(false);
            return;
         }

         console.log("Feedback submitted successfully");
         setShowFeedback(false);
         setFeedbackCompleted(true);
      } catch (err) {
         console.error("Error submitting feedback:", err);
      } finally {
         setIsSubmittingFeedback(false);
      }
   }, [currentBook, nextChapter, feedbackQuestion, feedbackRating]);

   // Component setup and cleanup
   useEffect(() => {
      mountedRef.current = true;

      return () => {
         mountedRef.current = false;

         // Close any open WebSocket connections
         if (wsRef.current) {
            try {
               wsRef.current.close(1000, "Component unmounting");
            } catch (err) {
               console.error("Error closing WebSocket during cleanup:", err);
            }
            wsRef.current = null;
         }
      };
   }, []);

   // Check permission when component becomes visible or book changes
   useEffect(() => {
      if (visible) {
         fetchFeedbackQuestion();
         checkPermissionAndStatus();
      } else {
         setShowPermissionMessage(false);
         setShowFeedback(false);
         setFeedbackCompleted(false);
      }
   }, [visible, currentBook, checkPermissionAndStatus, fetchFeedbackQuestion]);

   // WebSocket connection effect for chapter generation
   useEffect(() => {
      if (!generationTaskUuid || !isReadyForWs || isClosedRef.current) return;

      console.log(
         "Setting up WebSocket for chapter generation:",
         generationTaskUuid
      );

      const token = localStorage.getItem("token");
      if (!token) {
         console.error("No token found for WebSocket connection");
         return;
      }

      // Close existing connections
      if (wsRef.current) {
         console.log("Closing existing WebSocket connection");
         try {
            wsRef.current.close();
         } catch (err) {
            console.error("Error closing existing WebSocket:", err);
         }
         wsRef.current = null;
      }

      // Reset flags
      isClosedRef.current = false;
      intentionalCloseRef.current = false;
      isConnectedRef.current = false;

      // Connect to WebSocket
      const wsUrl = `${
         config.primaryWsUrl
      }/api/user/generate/ws/write_book?token=${encodeURIComponent(
         token
      )}&task_uuid=${encodeURIComponent(generationTaskUuid)}`;
      console.log("Connecting to WebSocket:", wsUrl);

      try {
         const ws = new WebSocket(wsUrl);
         wsRef.current = ws;

         ws.onopen = () => {
            console.log("WebSocket connected successfully");
            isConnectedRef.current = true;
         };

         ws.onmessage = (e) => {
            if (!mountedRef.current) return;

            try {
               const update = JSON.parse(e.data);
               console.log("WebSocket update:", update);

               if (update.status === "done") {
                  console.log("Chapter generation complete");
                  setFinalStatus({
                     status: "completed",
                     message: "Chapter generation completed successfully",
                  });

                  // Close the socket
                  intentionalCloseRef.current = true;
                  isClosedRef.current = true;

                  try {
                     ws.close(1000, "Task completed");
                  } catch (closeErr) {
                     console.error("Error closing WebSocket:", closeErr);
                  }

                  // Stop the loading spinner
                  setIsGenerating(false);

                  // Notify parent of successful generation
                  if (onGenerated) {
                     onGenerated(nextChapter);
                  }
               } else if (
                  update.status === "in_proses" ||
                  update.status === "update"
               ) {
                  setFinalStatus(update);

                  if (update.message && update.message !== "updated") {
                     setGenerationUpdates((prev) => [...prev, update.message]);
                  }
               } else if (
                  update.status === "failed" ||
                  update.status === "rejected"
               ) {
                  console.error("Generation failed:", update);
                  setFinalStatus(update);

                  // Set error and stop loading spinner
                  setError(update.message || "Generation failed");
                  setIsGenerating(false);
               }
            } catch (parseErr) {
               console.error("Error parsing WebSocket message:", parseErr);
            }
         };

         ws.onerror = (err) => {
            console.error("WebSocket error:", err);
            if (mountedRef.current) {
               setError("WebSocket connection error");
               setIsGenerating(false);
            }
         };

         ws.onclose = (event) => {
            console.log(
               `WebSocket closed: Code=${event.code}, Reason=${
                  event.reason || "No reason provided"
               }`
            );
            isClosedRef.current = true;
         };
      } catch (err) {
         console.error("Error creating WebSocket:", err);
         setError("Failed to connect to generation service");
         setIsGenerating(false);
      }

      return () => {
         if (wsRef.current) {
            try {
               wsRef.current.close(1000, "Effect cleanup");
            } catch (err) {
               console.error(
                  "Error closing WebSocket during effect cleanup:",
                  err
               );
            }
            wsRef.current = null;
         }
      };
   }, [generationTaskUuid, isReadyForWs, nextChapter, onGenerated]);

   // Prepare WebSocket when task UUID is set
   useEffect(() => {
      if (!generationTaskUuid) return;

      console.log("Setting up WebSocket delay for task:", generationTaskUuid);

      // Add a delay before enabling the WebSocket
      const timeoutId = setTimeout(() => {
         if (mountedRef.current) {
            console.log("WebSocket delay complete, ready to connect");
            setIsReadyForWs(true);
         }
      }, 500);

      return () => clearTimeout(timeoutId);
   }, [generationTaskUuid]);

   // Handler for generate next chapter button
   const handleGenerateChapter = useCallback(async () => {
      if (!nextChapter || !currentBook || !isAllowedToGenerate) return;

      try {
         setIsGenerating(true);
         setError(null);
         // Reset generation state
         setGenerationTaskUuid(null);
         setIsReadyForWs(false);
         setGenerationUpdates([]);
         setFinalStatus(null);

         console.log(
            `Generating chapter ${nextChapter} for book: ${
               currentBook?.title || "Unknown"
            }`
         );

         // Get auth token
         const token = localStorage.getItem("token");
         if (!token) {
            throw new Error("No authentication token found");
         }

         // Call write_book endpoint to generate next chapter
         const response = await fetch("/api/user/generate/write_book", {
            method: "POST",
            headers: {
               "Content-Type": "application/json",
               Authorization: "Bearer " + token,
            },
            body: JSON.stringify({ uuid: currentBook.uuid }),
         });

         if (!response.ok && response.status !== 202) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
               `Failed to start chapter generation: ${
                  errorData.error || response.status
               }`
            );
         }

         const data = await response.json();

         // Get task_uuid for tracking the generation
         const taskUuid = data.task_uuid;
         if (!taskUuid) {
            throw new Error("No task UUID returned from generation request");
         }

         console.log(`Generation task created with UUID: ${taskUuid}`);

         // Set the task UUID for tracking with WebSocket
         setGenerationTaskUuid(taskUuid);

         // WebSocket connection will be handled by the effect
      } catch (err) {
         console.error("Error generating chapter:", err);
         setError(err.message || "Failed to generate chapter");
         setIsGenerating(false);
      }
   }, [currentBook, nextChapter, isAllowedToGenerate]);

   // Handler for play next chapter button
   const handlePlayNext = useCallback(() => {
      console.log("=== PLAY NEXT BUTTON CLICKED ===");
      console.log(`Book: ${currentBook?.title}`);
      console.log(`Current chapter: ${nextChapter - 1}`);
      console.log(`Next chapter: ${nextChapter}`);
      console.log(`Next chapter exists: ${nextChapterExists}`);
      console.log(
         `Generated chapters: ${currentBook?.num_generated_chapters || 0}`
      );

      console.log("handlePlayNext called", {
         nextChapter,
         currentBook: currentBook?.title,
         nextChapterExists,
      });

      if (!nextChapter || !currentBook || !nextChapterExists) {
         console.log("Early return from handlePlayNext due to:", {
            missingNextChapter: !nextChapter,
            missingCurrentBook: !currentBook,
            missingNextChapterExists: !nextChapterExists,
         });
         return;
      }

      console.log(`🎬 PLAYING NEXT CHAPTER ${nextChapter} FROM BEGINNING`);

      if (onPlayNext) {
         // Pass the exact chapter number to play
         onPlayNext(nextChapter);
      }
   }, [currentBook, nextChapter, nextChapterExists, onPlayNext]);

   // Helper function to get status content for display
   const getStatusContent = useCallback(() => {
      if (!finalStatus) return null;

      switch (finalStatus.status) {
         case "waiting_in_queue":
            return {
               icon: "⏱️",
               title: "In Queue",
               message: `Queue position: ${finalStatus.queue_position}`,
            };

         case "in_proses":
         case "update":
            return {
               icon: "⚙️",
               title: "Generating",
               message: "Creating your chapter...",
            };

         case "completed":
            return {
               icon: "✅",
               title: "Generation Complete",
               message: finalStatus.message || "Your chapter is ready!",
            };

         case "failed":
            return {
               icon: "❌",
               title: "Generation Failed",
               message:
                  finalStatus.message || "An error occurred during generation",
            };

         case "rejected":
            return {
               icon: "⚠️",
               title: "Request Rejected",
               message:
                  finalStatus.message || "Generation request was rejected",
            };

         default:
            return {
               icon: "🔄",
               title: "Processing",
               message: "Processing your request...",
            };
      }
   }, [finalStatus]);

   // Don't render anything if not visible or no current book
   if (!visible || !nextChapter || !currentBook) {
      return null;
   }

   // Get current status content
   const statusContent = getStatusContent();

   // Define theme-specific colors
   const colors = {
      // Backgrounds
      feedbackBg: isLightTheme ? "#F8F9FA" : "#2D2D2D",
      cardBg: isLightTheme ? "#FFFFFF" : "#1E1E1E",
      headerBg: isLightTheme ? "#F1F5F9" : "#2D2D2D",
      detailsBg: isLightTheme ? "#F8FAFC" : "#1A1A1A",
      updatesBg: isLightTheme ? "#EEF2F6" : "#2D2D2D",
      permissionBg: isLightTheme ? "#E6F0FF" : "#1E3A5F",

      // Borders
      border: isLightTheme ? "#CBD5E1" : "#444",
      permissionBorder: isLightTheme ? "#90CAF9" : "#3B82F6",

      // Text
      primaryText: isLightTheme ? "#0A1629" : "#E1E1E1",
      secondaryText: isLightTheme ? "#64748B" : "#BBBBBB",
      mutedText: isLightTheme ? "#94A3B8" : "#999999",
      errorText: isLightTheme ? "#BE123C" : "#FF6B6B",
      permissionText: isLightTheme ? "#1E40AF" : "#90CAF9",

      // Interactive elements
      starActive: isLightTheme ? "#F59E0B" : "#FFB700", // Amber color
      starInactive: isLightTheme ? "#CBD5E1" : "#555",
      submitButtonBg: isLightTheme ? "#1E40AF" : "#4285F4",
      submitButtonDisabled: isLightTheme ? "#94A3B8" : "#555",
      playButtonBg: isLightTheme ? "#059669" : "#4CAF50", // Green
      generateButtonBg: isLightTheme ? "#1E40AF" : "#4285F4", // Blue

      // Shadows
      shadow: isLightTheme
         ? "0 2px 5px rgba(0, 0, 0, 0.08)"
         : "0 2px 5px rgba(0, 0, 0, 0.3)",
   };

   // Main render function
   return (
      <div
         style={{
            marginBottom: "15px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
         }}
      >
         {/* Feedback UI - shown as an addition, not blocking other components */}
         {showFeedback && !alreadyAnswered && !feedbackCompleted && (
            <div
               style={{
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: colors.feedbackBg,
                  padding: "12px",
                  borderRadius: "8px",
                  border: `1px solid ${colors.border}`,
                  width: "100%",
                  maxWidth: "400px",
                  boxShadow: colors.shadow,
               }}
            >
               <div
                  style={{
                     display: "flex",
                     flexDirection: "column",
                     alignItems: "center",
                     width: "100%",
                  }}
               >
                  <p
                     style={{
                        margin: 0,
                        color: colors.primaryText,
                        fontSize: "14px",
                        textAlign: "center",
                        marginBottom: "3px",
                        fontWeight: "500",
                     }}
                  >
                     {feedbackQuestion}
                  </p>

                  {/* Compact star rating */}
                  <div
                     style={{ display: "flex", gap: "5px", marginTop: "3px" }}
                  >
                     {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                           key={rating}
                           onClick={() => setFeedbackRating(rating)}
                           style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: "0",
                              fontSize: "24px",
                              color:
                                 rating <= feedbackRating
                                    ? colors.starActive
                                    : colors.starInactive,
                              transition: "transform 0.1s ease",
                           }}
                           onMouseOver={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                           }}
                           onMouseOut={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                           }}
                        >
                           ★
                        </button>
                     ))}
                  </div>

                  <button
                     onClick={submitFeedback}
                     disabled={feedbackRating === 0 || isSubmittingFeedback}
                     style={{
                        marginTop: "5px",
                        padding: "5px 12px",
                        backgroundColor:
                           feedbackRating > 0
                              ? colors.submitButtonBg
                              : colors.submitButtonDisabled,
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: feedbackRating > 0 ? "pointer" : "default",
                        fontSize: "13px",
                        fontWeight: "500",
                        opacity: isSubmittingFeedback ? 0.7 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                     }}
                  >
                     {isSubmittingFeedback ? (
                        <>
                           <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ animation: "spin 1s linear infinite" }}
                           >
                              <circle
                                 cx="12"
                                 cy="12"
                                 r="10"
                                 stroke="white"
                                 strokeWidth="2"
                                 strokeDasharray="30 60"
                              />
                              <style>{`
                                @keyframes spin {
                                    0% { transform: rotate(0deg); }
                                    100% { transform: rotate(360deg); }
                                }
                              `}</style>
                           </svg>
                           Submitting...
                        </>
                     ) : (
                        "Submit"
                     )}
                  </button>
               </div>
            </div>
         )}

         {/* Generation Status Panel - shown when generation is in progress */}
         {isGenerating && finalStatus && (
            <div
               style={{
                  width: "100%",
                  maxWidth: "400px",
                  marginBottom: "10px",
                  border: `1px solid ${colors.border}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: colors.cardBg,
                  boxShadow: colors.shadow,
               }}
            >
               {/* Status header (always visible) */}
               <div
                  onClick={() => setGenerationExpanded(!generationExpanded)}
                  style={{
                     padding: "8px 12px",
                     backgroundColor: colors.headerBg,
                     borderBottom: generationExpanded
                        ? `1px solid ${colors.border}`
                        : "none",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "space-between",
                     cursor: "pointer",
                     color: colors.primaryText,
                  }}
               >
                  <div
                     style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                     }}
                  >
                     {statusContent && (
                        <>
                           <span>{statusContent.icon}</span>
                           <span
                              style={{ fontWeight: "500", fontSize: "14px" }}
                           >
                              {statusContent.title}
                           </span>
                        </>
                     )}
                  </div>
                  <span
                     style={{ fontSize: "12px", color: colors.secondaryText }}
                  >
                     {generationExpanded ? "▲" : "▼"}
                  </span>
               </div>

               {/* Expanded status details */}
               {generationExpanded && (
                  <div
                     style={{
                        padding: "12px",
                        backgroundColor: colors.detailsBg,
                        fontSize: "13px",
                        color: colors.secondaryText,
                     }}
                  >
                     <p
                        style={{
                           margin: "0 0 8px 0",
                           color: colors.primaryText,
                        }}
                     >
                        {statusContent?.message || "Generating your chapter..."}
                     </p>

                     {generationUpdates.length > 0 && (
                        <div
                           style={{
                              maxHeight: "120px",
                              overflowY: "auto",
                              padding: "8px",
                              backgroundColor: colors.updatesBg,
                              borderRadius: "4px",
                              border: `1px solid ${colors.border}`,
                           }}
                        >
                           <ul
                              style={{
                                 margin: 0,
                                 paddingLeft: "20px",
                                 fontSize: "12px",
                                 color: colors.secondaryText,
                              }}
                           >
                              {generationUpdates.map((update, idx) => (
                                 <li key={idx}>{update}</li>
                              ))}
                           </ul>
                        </div>
                     )}
                  </div>
               )}
            </div>
         )}

         {/* Show the permission message if the user hasn't listened enough */}
         {showPermissionMessage &&
            !isAllowedToGenerate &&
            !nextChapterExists && (
               <div
                  style={{
                     marginBottom: "10px",
                     display: "flex",
                     flexDirection: "column",
                     alignItems: "center",
                     gap: "8px",
                  }}
               >
                  <p
                     style={{
                        margin: 0,
                        color: colors.secondaryText,
                        fontSize: "14px",
                        textAlign: "center",
                     }}
                  >
                     Chapter {nextChapter} hasn't been generated yet.
                  </p>
                  <div
                     style={{
                        padding: "8px",
                        backgroundColor: colors.permissionBg,
                        borderRadius: "4px",
                        border: `1px solid ${colors.permissionBorder}`,
                        width: "100%",
                        maxWidth: "400px",
                        textAlign: "center",
                        boxShadow: colors.shadow,
                     }}
                  >
                     <p
                        style={{
                           margin: "0",
                           color: colors.permissionText,
                           fontSize: "14px",
                        }}
                     >
                        <span
                           role="img"
                           aria-label="headphones"
                           style={{ marginRight: "5px" }}
                        >
                           🎧
                        </span>
                        Enjoy a bit more of this chapter to unlock the next one!
                     </p>
                  </div>
               </div>
            )}

         {/* Show "Play Next" button if the next chapter already exists */}
         {nextChapterExists && (
            <div
               style={{
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
               }}
            >
               <p
                  style={{
                     margin: 0,
                     color: colors.secondaryText,
                     fontSize: "14px",
                     textAlign: "center",
                  }}
               >
                  Ready for the next chapter?
               </p>

               <button
                  onClick={handlePlayNext}
                  style={{
                     display: "flex",
                     alignItems: "center",
                     gap: "6px",
                     backgroundColor: colors.playButtonBg,
                     color: "white",
                     border: "none",
                     borderRadius: "5px",
                     padding: "8px 14px",
                     fontWeight: "500",
                     cursor: "pointer",
                     boxShadow: colors.shadow,
                  }}
               >
                  <svg
                     width="18"
                     height="18"
                     viewBox="0 0 24 24"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path d="M6 4L20 12L6 20V4Z" fill="white" />
                  </svg>
                  Play Chapter {nextChapter}
               </button>
            </div>
         )}

         {/* Show the generate button if needed */}
         {!nextChapterExists && isAllowedToGenerate && !isGenerating && (
            <div
               style={{
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
               }}
            >
               <p
                  style={{
                     margin: 0,
                     color: colors.secondaryText,
                     fontSize: "14px",
                     textAlign: "center",
                  }}
               >
                  Chapter {nextChapter} hasn't been generated yet.
               </p>

               {error && (
                  <p
                     style={{
                        margin: 0,
                        color: colors.errorText,
                        fontSize: "13px",
                        textAlign: "center",
                     }}
                  >
                     Error: {error}
                  </p>
               )}

               <button
                  onClick={handleGenerateChapter}
                  disabled={isGenerating}
                  style={{
                     display: "flex",
                     alignItems: "center",
                     gap: "6px",
                     backgroundColor: colors.generateButtonBg,
                     color: "white",
                     border: "none",
                     borderRadius: "5px",
                     padding: "8px 14px",
                     fontWeight: "500",
                     cursor: isGenerating ? "default" : "pointer",
                     opacity: isGenerating ? 0.7 : 1,
                     boxShadow: colors.shadow,
                  }}
               >
                  <svg
                     width="18"
                     height="18"
                     viewBox="0 0 24 24"
                     fill="none"
                     xmlns="http://www.w3.org/2000/svg"
                  >
                     <path
                        d="M12 5V19M5 12H19"
                        stroke="white"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                     />
                  </svg>
                  Generate Chapter {nextChapter}
               </button>
            </div>
         )}
      </div>
   );
};

export default AudioPlayerNextChapter;
